<script setup lang="ts">
import { ref, watch, type WatchStopHandle, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  CheckoutAction,
  InputWithLabel,
} from "@/scripts/application/components";
import { useUserProfile } from "@/scripts/application/composables";
import type { ICourse } from "@/scripts/application/types";

const props = defineProps<{
  course: ICourse;
}>();

const { t } = useI18n();
const {
  user_profile,
  user_profile_errors,
  updateUserProfile,
  error: profileError,
} = useUserProfile();

const showDOBField = computed(
  () =>
    props.course.certificate_conditions?.some(
      (c) => c.type === "CertificateConditions::StudentDOBPresentRule"
    ) ?? false
);
const showSSNField = computed(
  () =>
    props.course.certificate_conditions?.some(
      (c) => c.type === "CertificateConditions::StudentSSNPresentRule"
    ) ?? false
);
const showExternalIDField = computed(
  () =>
    props.course.certificate_conditions?.some(
      (c) => c.type === "CertificateConditions::StudentExternalIDPresentRule"
    ) ?? false
);

const loading = ref(false);
const disableFirstName = ref(false);
const disableLastName = ref(false);
const disableEmail = ref(false);
const disableTelephone = ref(false);
const disableDOB = ref(false);
const disableSSN = ref(false);
const disableExternalID = ref(false);

const signOutPath = "/my/users/sign_out";

let stopWatch: WatchStopHandle;
stopWatch = watch(
  user_profile,
  (val) => {
    if (val) {
      disableFirstName.value = !!val.first_name;
      disableLastName.value = !!val.last_name;
      disableEmail.value = !!val.email;
      disableTelephone.value = !!val.telephone_number;
      disableDOB.value = !!val.date_of_birth;
      disableSSN.value = !!val.ssn;
      disableExternalID.value = !!val.external_id;
      if(stopWatch) stopWatch();
    }
  },
  { immediate: true }
);

const emit = defineEmits<{
  (event: "loading", value: boolean): void;
  (event: "done"): void;
}>();

const updateProfile = () => {
  emit("loading", true);
  loading.value = true;
  updateUserProfile()
    .then(() => {
      if (!profileError.value) emit("done");
      emit("loading", false);
      loading.value = false;
    })
    .catch(() => {
      emit("loading", false);
      loading.value = false;
    });
};
</script>
<template>
  <div class="flex-1">
    <div class="pb-8">
      <h1 class="text-3xl sm:text-5xl font-extrabold text-custom-text">
        {{ t("booking") }}
      </h1>
    </div>
    <div class="pt-5">
      <h3 class="text-xl font-bold mb-4 text-custom-grey-1">
        {{ t("participant_information") }}
      </h3>
      <form @submit.prevent="updateProfile" v-if="user_profile">
        <div
          class="pt-4 lg:flex justify-between space-y-4 lg:space-y-0 lg:space-x-3"
        >
          <input-with-label
            required
            type="text"
            v-model="user_profile.first_name"
            :error="user_profile_errors.first_name?.[0]"
            name="first_name"
            :disabled="disableFirstName"
          >
            {{ t("first_name") }}
          </input-with-label>
          <input-with-label
            required
            v-model="user_profile.last_name"
            name="last_name"
            :error="user_profile_errors.last_name?.[0]"
            :disabled="disableLastName"
          >
            {{ t("last_name") }}
          </input-with-label>
        </div>
        <div class="pt-4">
          <input-with-label
            type="email"
            v-model="user_profile.email"
            name="email"
            :disabled="disableEmail"
          >
            {{ t("email") }}
          </input-with-label>
        </div>
        <div class="pt-4">
          <input-with-label
            type="text"
            required
            v-model="user_profile.telephone_number"
            name="telephone_number"
            :error="user_profile_errors.telephone_number?.[0]"
            :disabled="disableTelephone"
          >
            {{ t("mobile_number") }}
          </input-with-label>
        </div>
        <div class="pt-4" v-if="showDOBField">
          <input-with-label
            type="date"
            required
            v-model="user_profile.date_of_birth"
            name="date_of_birth"
            :disabled="disableDOB"
          >
            {{ t("date_of_birth") }}
          </input-with-label>
        </div>
        <div class="pt-4" v-if="showSSNField">
          <input-with-label
            type="text"
            v-model="user_profile.ssn"
            name="ssn"
            :disabled="disableSSN"
            :optional="true"
          >
            {{ t("ssn") }}
          </input-with-label>
        </div>
        <div class="pt-4" v-if="showExternalIDField">
          <input-with-label
            type="text"
            required
            v-model="user_profile.external_id"
            name="external_id"
            :disabled="disableExternalID"
          >
            {{ t("external_id") }}
          </input-with-label>
        </div>
        <div
          class="z-10 fixed bottom-0 left-0 right-0 px-4 py-2 shadow-custom-shadow bg-white sm:static sm:shadow-none sm:p-0"
        >
          <p class="text-sm mt-4">
            {{ t("not_user", { name: user_profile.first_name }) }}
            <a
              :href="signOutPath"
              data-method="delete"
              class="text-primary underline ml-1"
            >
              {{ t("log_out") }}
            </a>
          </p>
          <div class="sm:pt-10">
            <checkout-action :disabled="loading">
              <span class="leading-none"> {{ t("next") }} </span>
              <svg class="svg-icon svg-chevron-right">
                <use
                  xlink:href="#chevron-right"
                  width="100%"
                  height="100%"
                ></use>
              </svg>
            </checkout-action>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
<i18n lang="yaml">
en:
  booking: "Booking"
  back: "Back"
  first_name: "First name"
  last_name: "Last name"
  participant_information: Fill in the details of the course participant
  email: "Email"
  mobile_number: Mobile number
  date_of_birth: Date of birth
  ssn: Social security number
  external_id: Student ID number (BI)
  not_user: "Not %{name}?"
  log_out: "Log out"
  next: "Next"
nb:
  booking: "Påmelding"
  back: "Tilbake"
  first_name: "Fornavn"
  last_name: "Etternavn"
  participant_information: Informasjon om kursdeltaker
  email: "E-post"
  mobile_number: Mobilnummer
  date_of_birth: Fødselsdato
  ssn: Personnummer
  external_id: Studentnummer (BI)
  not_user: "Ikke %{name}?"
  log_out: "Logg ut"
  next: "Neste"
uk:
  booking: "Бронювання"
  back: "Назад"
  first_name: "Ім'я"
  last_name: "Прізвище"
  participant_information: Заповніть дані про учасника курсу
  email: "Електронна пошта"
  mobile_number: Мобільний номер
  date_of_birth: Дата народження
  ssn: ІПН
  external_id: Зовнішній ID (BI)
  not_user: "Не %{name}?"
  log_out: "Вийти та увійти знову"
  next: "Далі"
es:
  booking: "Inscripción"
  back: "Volver"
  first_name: "Nombre"
  last_name: "Apellido"
  participant_information: Rellene los datos del participante del curso
  email: "Correo electrónico"
  mobile_number: Número de teléfono móvil
  date_of_birth: Fecha de nacimiento
  ssn: Número de identificación fiscal
  external_id: ID externo BI
  not_user: "¿No es %{name}?"
  log_out: "Cerrar sesión"
  next: "Siguiente"
pl:
  booking: "Rezerwacja"
  back: "Wstecz"
  first_name: "Imię"
  last_name: "Nazwisko"
  participant_information: Wypełnij dane uczestnika kursu
  email: "E-mail"
  mobile_number: Numer telefonu komórkowego
  date_of_birth: Data urodzenia
  ssn: PESEL
  external_id: Zewnętrzny ID (BI)
  not_user: "Nie %{name}?"
  log_out: "Wyloguj się i zaloguj ponownie"
  next: "Dalej"
</i18n>
